import React, { useEffect, useMemo, useState } from 'react';
import '../styles/Home.css';
import '../styles/Review.css';
import { FaEnvelope, FaMapPin, FaPhone } from 'react-icons/fa';
import { FaChevronLeft, FaChevronRight, FaFacebook, FaGlobe, FaInstagram, FaTiktok } from 'react-icons/fa6';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { getAmenityIcon } from '../constants/amenityIcons';
import ContactWebsite from '../components/ContactWebsite';
import VenueFeatureCards from '../components/VenueFeatureCards';
import axiosPost from '../helpers/axios-helper';
import MapDisplay from '../components/MapDisplay';
import ImageLightbox from '../components/ImageLightbox';
import config from '../constants/config';
import VenueButtons from '../components/VenueButtons';
import BackButton from '../components/BackButton';

export default function Restaurant() {
  const location = useLocation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const [restaurantId, setRestaurantId] = useState(Number(queryParams.get('id')) || 0);

  const slugify = (str) => {
    try {
      return (str || '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    } catch { return ''; }
  };

  const [review, setReview] = useState({
    id: restaurantId,
    type: 'restaurant',
    title: '',
    heroImage: '',
    contact: {
      address: '',
      phone: '',
      email: '',
      website: ''
    },
    socials: {
      instagram: { handle: '', url: '' },
      facebook: { handle: '', url: '' },
      tiktok: { handle: '', url: '' }
    },
    amenities: [],
    content: '',
    sections: [],
    more: [],
    subcategory: '',
    queerOwned: false,
    mapLocation: null
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [heroIndex, setHeroIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const extrasByKey = useMemo(() => ({}), []);

  useEffect(() => {
    // Resolve restaurantId from slug if no id provided
    if (!restaurantId && slug) {
      const controller = new AbortController();
      axiosPost(
        'restaurants/list',
        { 'Content-Type': 'application/json' },
        { page: 1, per_page: 1000 },
        (resp) => {
          const items = Array.isArray(resp?.data?.items) ? resp.data.items : [];
          const found = items.find(it => slugify(it.title || '') === slug);
          if (found?.id) setRestaurantId(Number(found.id));
        },
        controller
      );
      return () => controller.abort();
    }
    if (!restaurantId) return;

    const controller = new AbortController();
    axiosPost(
      'restaurants/get',
      { 'Content-Type': 'application/json' },
      { id: restaurantId },
      (resp) => {
        if (resp?.success && resp?.data?.restaurant) {
          const r = resp.data.restaurant;
          const extras = Array.isArray(r?.extras) ? r.extras : [];
          const map = {};
          extras.forEach(e => { if (e?.section_key) map[e.section_key] = e; });

          // Derive fields
          const overviewHtml = (map.overview?.description || r.description || '').trim();
          const heroImage = r.image || '';
          // Build images array from optional r.images (newline/comma) plus fallback to single image
          const parseImages = (val) => {
            if (!val) return [];
            if (Array.isArray(val)) return val.map(v => (v ?? '').toString().trim()).filter(Boolean);
            const str = typeof val === 'string' ? val : String(val);
            return str
              .split(/\r?\n|,/)
              .map(s => s.trim())
              .filter(Boolean);
          };
          let images = parseImages(r.images || '');
          if (!images.length && heroImage) images = [heroImage];
          // Build dynamic sections from extras (excluding 'overview')
          const sections = extras
            .filter(e => (e?.section_key || '').toLowerCase() !== 'overview')
            .filter(e => (e?.description || '').trim() !== '')
            .sort((a, b) => {
              const soA = (a?.sort_order ?? 0);
              const soB = (b?.sort_order ?? 0);
              if (soA !== soB) return soA - soB;
              const ka = (a?.section_key || '').toLowerCase();
              const kb = (b?.section_key || '').toLowerCase();
              return ka.localeCompare(kb);
            })
            .map(e => ({
              key: e.section_key,
              title: (e.title && e.title.trim()) || (e.section_key || '')
                .replace(/_/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase()),
              html: e.description
            }));

          const more = [];

          // Parse amenities value into chips
          const rawAmenities = r.amenities;
          let amenities = [];
          if (Array.isArray(rawAmenities)) {
            amenities = rawAmenities
              .map(s => (s ?? '').toString().trim())
              .filter(Boolean)
              .map(am => ({ amenity: am }));
          } else if (typeof rawAmenities === 'string') {
            const str = rawAmenities.trim();
            amenities = str
              ? str
                  .split(/\r?\n|,/)
                  .map(s => s.trim())
                  .filter(Boolean)
                  .map(am => ({ amenity: am }))
              : [];
          } else if (rawAmenities != null && rawAmenities !== undefined) {
            const str = String(rawAmenities).trim();
            amenities = str
              ? str
                  .split(/\r?\n|,/)
                  .map(s => s.trim())
                  .filter(Boolean)
                  .map(am => ({ amenity: am }))
              : [];
          }

          // Parse location JSON if it exists
          let mapLocation = null;
          try {
            if (r.location && typeof r.location === 'string' && r.location.trim().startsWith('{')) {
              mapLocation = JSON.parse(r.location);
            }
          } catch (e) {
            console.warn('Failed to parse location JSON', e);
          }

          setReview({
            id: r.id,
            type: 'restaurant',
            title: r.title || '',
            subcategory: r.subcategory || '',
            heroImage,
            images,
            contact: {
              address: r.address || r.location || '',
              phone: r.phone || '',
              email: r.email || '',
              website: r.website || ''
            },
            socials: {
              instagram: { handle: '', url: r.instagram_url || '' },
              facebook: { handle: '', url: r.facebook_url || '' },
              tiktok: { handle: '', url: r.tiktok_url || '' }
            },
            amenities,
            content: overviewHtml ? `<bold>Overview</bold><br/><br/>${overviewHtml}` : '',
            sections,
            more,
            queerOwned: (Number(r.queer_owned || 0) === 1) || (r.queer_owned === true),
            mapLocation
          });

          // Redirect legacy /restaurant?id=... to canonical /restaurant/:slug
          if (!slug && queryParams.get('id')) {
            const s = slugify(r.title || '');
            if (s) navigate(`/restaurant/${s}`, { replace: true });
          }
        }
      },
      controller
    );

    return () => controller.abort();
  }, [restaurantId, slug]);

  const nextSlide = () => {
    if (!review.more?.length) return;
    setCurrentIndex((prev) => (prev + 1) % review.more.length);
  };

  const prevSlide = () => {
    if (!review.more?.length) return;
    setCurrentIndex((prev) => (prev - 1 + review.more.length) % review.more.length);
  };

  const nextHero = () => {
    const len = review.images?.length || 0;
    if (len <= 1) return;
    setHeroIndex((prev) => (prev + 1) % len);
  };

  const prevHero = () => {
    const len = review.images?.length || 0;
    if (len <= 1) return;
    setHeroIndex((prev) => (prev - 1 + len) % len);
  };

  return (
    <div style={{ paddingTop: '90px' }}>
      <div className='container'>
        <div className='sponsored-banner'></div>
        <div className='sponsored-title'>Sponsored banner</div>
      </div>

      <div className='container'>
        <BackButton />
        <div className='review-header'>
          <div className='review-header-info'>
            {review.subcategory && (
              <p className='venue-subcategory'>{review.subcategory}</p>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 className='review-title'>{review.title}</h1>
              {review.queerOwned && (
                <div
                  title='Queer Owned'
                  style={{
                    background: 'linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(90deg, #e91e63, #9c27b0) border-box',
                    border: '1px solid transparent',
                    color: '#2c2c2c',
                    padding: '4px 10px',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    marginTop: 5
                  }}
                >
                  Queer Owned
                </div>
              )}
            </div>
            {(review.contact.address) && (
              <div className='hotel-location'>
                <FaMapPin size={12} color={'#2c2c2c'} />
                <div className='hotel-address'>{String(review.contact.address).toUpperCase()}</div>
              </div>
            )}
          </div>

          <div className='review-header-actions'>

            {(review.contact.website) && (
              <a className='review-header-btn' href={review.contact.website} target='_blank' rel='noreferrer'>
                Visit Website
              </a>
            )}
          </div>
        </div>
      </div>

      <div className={`review-gallery-grid ${review.images?.length <= 1 ? 'single-image' : ''} ${review.images?.length === 2 ? 'two-images' : ''}`}>
        <div 
          className='review-gallery-main-image'
          style={{ backgroundImage: `url('${review.images?.[0] || review.heroImage || ''}')` }}
          onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
        >
          {review.images?.length > 0 && (
            <div className='review-gallery-count'>
              <span style={{ marginRight: 6 }}>📷</span> {review.images.length}
            </div>
          )}
        </div>
        {review.images?.length > 1 && (
          <div className='review-gallery-side'>
            {review.images.slice(1, 4).map((img, idx) => (
              <div 
                key={idx}
                className='review-gallery-side-image'
                style={{ backgroundImage: `url('${img}')` }}
                onClick={() => { setLightboxIndex(idx + 1); setLightboxOpen(true); }}
              >
                {idx === 2 && review.images.length > 4 && (
                  <div className='review-gallery-more-overlay'>
                    +{review.images.length - 4} more
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {lightboxOpen && review.images?.length > 0 && (
        <ImageLightbox
          images={review.images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      <div className='container'>
        <div className='review-content-splitter'>
          <div className='review-content'>
            <VenueButtons itemType='restaurant' itemId={review.id} />

            <VenueFeatureCards
              itemType='restaurant'
              itemId={review.id}
              venuePath={`/restaurant/${slugify(review.title)}`}
              venueTitle={review.title}
            />

            {review.amenities?.length > 0 && (
              <div className='review-amenities'>
                <div className='review-amenities-title'>AMENITIES</div>
                <div className='review-amenities-container'>
                  {review.amenities.map((am, index) => {
                    const Icon = getAmenityIcon(am.amenity);
                    return (
                      <div className='ammenity' key={index}>
                        <Icon size={14} color={'#2c2c2c'} />
                        <span>{am.amenity}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {review.content && <div className='review-amenities-title'>Overview</div>}
            {review.content && (
              <div
                className='rich-text'
                style={{ letterSpacing: '1px', fontSize: 14, color: '#2b2b2b', marginBottom: 50, lineHeight: '1.5' }}
                dangerouslySetInnerHTML={{
                  __html: review.content
                    .replace(/<bold>\s*Overview\s*<\/bold>\s*<br\s*\/?><br\s*\/?/i, '')
                    .replace(/<bold>/g, '<strong>')
                    .replace(/<\/bold>/g, '</strong>')
                    .replace(/color:\s*[^;"}]+;?/gi, '')
                }}
              />
            )}

            {review.sections?.map((sec, idx) => (
              <div key={sec.key || idx}>
                <div className='review-amenities-title'>{sec.title}</div>
                <div
                  className='rich-text'
                  style={{ letterSpacing: '1px', fontSize: 14, color: '#2b2b2b', marginBottom: 40, lineHeight: '1.5' }}
                  dangerouslySetInnerHTML={{
                    __html: (sec.html || '')
                      .replace(/<bold>/g, '<strong>')
                      .replace(/<\/bold>/g, '</strong>')
                      .replace(/color:\s*[^;"}]+;?/gi, '')
                  }}
                />
              </div>
            ))}

            {review.more?.length > 0 && (
              <>
                <div className='review-amenities-title'>Also found in {review.title}</div>
                <div className='review-content-more'>
                  <div className='review-content-more-btn' onClick={prevSlide}>
                    <FaChevronLeft size={16} color='#fff' />
                  </div>

                  <div className='review-content-more-main' style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
                    {review.more.map((item, index) => (
                      <div className='review-content-more-item' key={index}>
                        <div className='review-content-more-item-image' style={{ backgroundImage: `url(${item.image})` }} />
                        <div className='review-content-more-item-info'>
                          <div className='review-content-more-item-info-title'>{item.name}</div>
                          <div className='review-content-more-item-info-sub'>{item.about}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className='review-content-more-btn-alt' onClick={nextSlide}>
                    <FaChevronRight size={16} color='#fff' />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className='review-side-content'>
            <div className='review-location-title'>RESTAURANT LOCATION</div>
            {review.mapLocation && review.mapLocation.lat && review.mapLocation.lng ? (
              <MapDisplay
                location={review.mapLocation}
                apiKey={config.googleMaps.apiKey}
                title={review.title}
                height="300px"
              />
            ) : (
              <div className='review-location-map'></div>
            )}

            {(review.contact.phone || review.contact.email || review.contact.website) && (
              <div className='review-contact-title'>CONTACT INFORMATION</div>
            )}
            {review.contact.phone && (
              <div className='review-contact-item'>
                <FaPhone size={16} color='#2c2c2c' />
                <div className='review-contact-text'>{review.contact.phone}</div>
              </div>
            )}
            {review.contact.email && (
              <div className='review-contact-item'>
                <FaEnvelope size={16} color='#2c2c2c' />
                <div className='review-contact-text'>{review.contact.email}</div>
              </div>
            )}
            {review.contact.website && (
              <div className='review-contact-item'>
                <FaGlobe size={16} color='#2c2c2c' />
                <ContactWebsite url={review.contact.website} />
              </div>
            )}

            {(review.socials?.instagram?.url || review.socials?.facebook?.url || review.socials?.tiktok?.url) && (
              <>
                <div className='review-contact-title'>SOCIAL MEDIA</div>
                <div className='review-social-icons'>
                  {review.socials?.instagram?.url && (
                    <a href={review.socials.instagram.url} target='_blank' rel='noreferrer' className='review-social-icon'>
                      <FaInstagram size={20} color='#2c2c2c' />
                    </a>
                  )}
                  {review.socials?.facebook?.url && (
                    <a href={review.socials.facebook.url} target='_blank' rel='noreferrer' className='review-social-icon'>
                      <FaFacebook size={20} color='#2c2c2c' />
                    </a>
                  )}
                  {review.socials?.tiktok?.url && (
                    <a href={review.socials.tiktok.url} target='_blank' rel='noreferrer' className='review-social-icon'>
                      <FaTiktok size={20} color='#2c2c2c' />
                    </a>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
